import {
  ServerEventDto,
  ServerEventsDto,
  ServerLiveEventDto,
} from "../../api/interfaces/serverEventDto";
import { mapServerEventToUi } from "../mappers/mapServerEventToUi";
import { useNotificationsStore } from "../store/notificationsStore";
import { handleAvailableAttemptsEvent } from "../handlers/handleAvailableAttempts";

let ws: WebSocket | null = null;
let reconnectTimer: number | null = null;
let currentUrl: string | null = null;

function scheduleReconnect() {
  if (!currentUrl || reconnectTimer) return;

  const url = currentUrl;

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect(url);
  }, 2000);
}

function isSnapshotMessage(data: unknown): data is ServerEventsDto {
  return (
    typeof data === "object" &&
    data !== null &&
    "events" in data &&
    Array.isArray((data as ServerEventsDto).events)
  );
}

function isLiveEventMessage(data: unknown): data is ServerLiveEventDto {
  return (
    typeof data === "object" &&
    data !== null &&
    "event_id" in data &&
    !("events" in data)
  );
}

export function connect(url: string) {
  if (ws) return;

  currentUrl = url;
  ws = new WebSocket(url);

  ws.onopen = () => {
    console.log("[notifications ws] connected");
  };

  ws.onmessage = (event) => {
    try {
      const raw = JSON.parse(event.data);

      // 1. Первый снимок при подключении
      if (isSnapshotMessage(raw)) {
        const mappedItems = raw.events
          .map((dto) => mapServerEventToUi(dto))
          .filter((item): item is NonNullable<typeof item> => item !== null);

        useNotificationsStore.getState().syncFromSocket({
          items: mappedItems,
          unreadCount: raw.count_unread_events,
        });

        raw.events.forEach((event) => {
          if (event.type === "AVAILABLE_ATTEMPTS") {
            handleAvailableAttemptsEvent(event);
          }
        });

        return;
      }

      // 2. Новое live-уведомление
      if (isLiveEventMessage(raw)) {
        const mappedItem = mapServerEventToUi(raw as ServerEventDto);

        if (mappedItem) {
          useNotificationsStore.getState().addOrUpdate(mappedItem);

          useNotificationsStore.getState().setLastLiveEvent({
            id: mappedItem.id,
            type: raw.type,
            requestUuid: mappedItem.requestUuid,
          });
        }

        if (typeof raw.count_unread_events === "number") {
          useNotificationsStore
            .getState()
            .setUnreadCount(raw.count_unread_events);
        }

        if (raw.type === "AVAILABLE_ATTEMPTS") {
          handleAvailableAttemptsEvent(raw);
        }

        return;
      }

      console.warn("[notifications ws] unknown message format", raw);
    } catch (error) {
      console.error("[notifications ws] parse error", error);
    }
  };

  ws.onerror = (error) => {
    console.error("[notifications ws] error", error);
  };

  ws.onclose = () => {
    console.warn("[notifications ws] closed");
    ws = null;
    scheduleReconnect();
  };
}

export function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  currentUrl = null;
  ws?.close();
  ws = null;
}

export function markRead(id: string) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;

  const payload = {
    action: "mark_read",
    event_id: Number(id),
  };

  ws.send(JSON.stringify(payload));
  return true;
}

export const notificationsSocket = {
  connect,
  disconnect,
  markRead,
};
